<template>
    <div class="course__edit">
        <div class="course__edit_header">
            <view-actions
                :cancel-button-active="isRouteCourse"
                :cancel-button-text="'Discard draft'"
                :cancel-notify-active="true"
                :cancel-notify-content-text="'This action will remove all changes. Continue?'"
                :cancel-notify-submit-text="'Discard draft'"
                :custom-button-active="isRouteCourse"
                :custom-button-disabled="courseFilesUploadingProcess"
                :custom-button-text="'Save draft'"
                :custom-button-animate-loading="true"
                :custom-button-animate-loading-event-name="'course-save'"
                :notify-title="'draft'"
                :notify-process="'stop editing'"
                :submit-button-animate-loading="isRouteCatalogItem && user.isLearningExperienceAdministrator()"
                :submit-button-animate-loading-event-name="'catalog-item-save'"
                :submit-button-active="(isRouteCourse && user.isCourseCreator()) || (isRouteCatalogItem && user.isLearningExperienceAdministrator()) ? true : false"
                :submit-button-disabled="courseFilesUploadingProcess"
                :submit-button-text="isRouteCourse ? 'Save & release' : 'Save changes'"
                :title="`Editing: ${courseDefaultName}`"
                v-on="isRouteCourse ? { 'emit-submit': openReleaseNotify } : { 'emit-submit': performSubmitValidate }"
                @emit-notify-cancel-submit="courseStopEdit"
                @emit-custom="performSubmitValidate"
            />
        </div>
        <div class="course__edit_main">
            <div class="container fullheight">
                <div class="columns is-block-mobile fullheight m-t-b-0">
                    <div class="column is-narrow p-t-b-0">
                        <component-sidebar :nav-items="sidebarNav"></component-sidebar>
                    </div>
                    <div class="column p-t-b-0">
                        <section class="course__edit_main_content">
                            <component-text :class="'course__edit_main_content_savedinfo'">Editing...</component-text>
                            <keep-alive>
                                <transition name="fade" mode="out-in">
                                    <router-view/>
                                </transition>
                            </keep-alive>
                        </section>
                    </div>
                </div>
            </div>
        </div>
        <pop-notify
            ref="popnotifyvalidation"
            :class="'pop__notify--validation'"
            :title-text="`Something's not quite right`"
            :content-text="'Please check that you have filled out all the fields in the form correctly and try again.'"
            :cancel-button-text="'Ok'"
            :submit-button-active="false"
        />
        <pop-notify
            ref="popnotifyrelease"
            :cancel-button-text="'Cancel'"
            :class="'pop__notify--release'"
            :content-text="'Release generates new public version from recently saved draft. Changes are automatically delivered to all customers.'"
            :form-active="true"
            :form-fields="releaseNotifyFormFields"
            :prevent-close="true"
            :size="'medium'"
            :submit-button-text="'Release'"
            :submit-button-animate-loading="true"
            :submit-button-animate-loading-name="'course-save'"
            :title-text="'Release course'"
            @emit-form="handleReleaseNotifyFormInput"
            @emit-submit="performSubmitValidate({ payload: { courseId: courseId, release: true } })"
        />
    </div>
</template>

<script>

import ViewActions from '@/components/default/shared/ViewActions'
import ComponentText from '@/components/default/shared/ComponentText'
import PopNotify from '@/components/default/shared/PopNotify'
import { validateBeforeRouteLeave, performSubmitValidate } from '@/helpers/mixins'
import { User } from '@/User'
import ComponentSidebar from '@/components/default/shared/ComponentSidebar'

export default {
  name: 'course-edit',
  components: {
    ComponentSidebar,
    PopNotify,
    ComponentText,
    ViewActions
  },
  data () {
    return {
      user: new User()
    }
  },
  mixins: [validateBeforeRouteLeave, performSubmitValidate],
  computed: {
    courseId () {
      return this.$store.getters.getEbsCourseId
    },
    courseCatalogId () {
      return this.$route.params.catalogId
    },
    courseDefaultName () {
      return this.$store.getters.getEbsCourseDefaultName
    },
    courseIsDraftVersion () {
      return this.$store.getters.getEbsCourseIsDraftVersion
    },
    courseHasBeenReleased () {
      return this.$store.getters.getEbsCourseHasBeenReleased
    },
    courseReleaseNotes () {
      return this.$store.getters.getEbsCourseReleaseNotes
    },
    courseInstantOffer () {
      return this.$store.getters.getEbsCourseInstantOffer
    },
    courseFilesUploadingProcess () {
      return this.isRouteCourse ? this.$store.getters.getEbsCourseFilesUploading : this.$store.getters.getEbsCatalogItemFilesUploading
    },
    //
    releaseNotifyFormFields () {
      let fieldsArr = []

      fieldsArr.push({
        name: 'release notes',
        type: 'editor',
        label: 'Release notes',
        subLabel: 'Release notes can be seen by administrators on all version updates.',
        answers: this.courseReleaseNotes,
        validation: { mandatory: true }
      })
      if (!this.courseHasBeenReleased) {
        fieldsArr.push({
          name: 'course availability',
          type: 'checkbox',
          label: 'Automatically create an offer to let Learning Experience Administrators make this course available for learners in my workspaces.',
          subLabel: '',
          answers: this.courseInstantOffer,
          validation: { mandatory: false }
        })
      }

      return fieldsArr
    },
    //
    rootItemName () {
      return this.isRouteCourse ? 'course' : 'catalog-item'
    },
    nameActionCreate () {
      return 'ebsCourseCreate'
    },
    nameActionUpdate () {
      return this.isRouteCourse ? 'ebsCourseUpdate' : 'ebsCatalogItemUpdate'
    },
    paramsActionUpdate () {
      return this.isRouteCourse ? { courseId: this.courseId } : { catalogId: this.courseCatalogId }
    },
    nameActionReset () {
      return 'ebsCourseReset'
    },
    isRouteCourse () {
      return this.$route.name.includes('ebs-course-edit') || this.$route.name.includes('ebs-course-add')
    },
    isRouteCatalogItem () {
      return this.$route.name.includes('ebs-catalog-item-edit')
    },
    routeNameBase () {
      let routeName = this.$route.name
      let nameStr

      nameStr = routeName.substring(0, routeName.lastIndexOf('-'))

      return nameStr
    },
    sidebarNav () {
      let navArr = []
      navArr.push({ label: 'Course details', name: `${this.routeNameBase}-details`, visible: () => true })
      navArr.push({ label: 'Modules', name: `${this.routeNameBase}-modules`, visible: () => true })
      if (this.isRouteCatalogItem) {
        navArr.push({ label: 'Enrolment type & visibility', name: `${this.routeNameBase}-enrolment`, visible: () => true })
        navArr.push({ label: 'Certificate', name: `${this.routeNameBase}-certificate`, visible: () => true })
        navArr.push({ label: 'Feedback form', name: `${this.routeNameBase}-feedback`, visible: () => true })
        navArr.push({ label: 'Email notifications', name: `${this.routeNameBase}-notifications`, visible: () => true })
      }
      navArr.push({ label: 'Attachments', name: `${this.routeNameBase}-attachments`, visible: () => true })
      navArr.push({ label: 'Useful links', name: `${this.routeNameBase}-links`, visible: () => true })
      if (this.isRouteCourse) {
        navArr.push({ label: 'Versions', name: `${this.routeNameBase}-versions`, visible: () => true })
      }

      return navArr
    }
  },
  methods: {
    openReleaseNotify () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.$refs.popnotifyrelease.openNotify()
        } else {
          this.$refs.popnotifyvalidation.openNotify()
        }
      })
    },
    performSubmitValidateValidationCallback () {
      this.$eventBus.$emit('stop-course-save')
      this.$eventBus.$emit('stop-catalog-item-save')
    },
    performSubmitValidateFinallyCallback () {
      this.$eventBus.$emit('stop-course-save')
      this.$eventBus.$emit('stop-catalog-item-save')
    },
    performSubmitValidateThenCallback () {
      if (this.$refs.popnotifyrelease) {
        this.$refs.popnotifyrelease.closeNotify()
      }
    },
    allowRedirectBeforeRouteLeave () {
      this.$eventBus.$emit('course-submit')
    },
    //
    courseStopEdit () {
      this.allowRedirectBeforeRouteLeave()
      this.$router.push({ name: 'ebs-course-list' })
    },
    handleReleaseNotifyFormInput (data) {
      if (data.index === 0) {
        this.setCourseReleaseNotes(data.value)
      } else if (data.index === 1) {
        this.setCourseInstantOffer(data.value)
      }
    },
    setCourseReleaseNotes (data) {
      this.$store.dispatch('ebsCourseSetReleaseNotes', data)
    },
    setCourseInstantOffer (data) {
      this.$store.dispatch('ebsCourseSetInstantOffer', data)
    }
  },
  provide () {
    return {
      $validator: this.$validator
    }
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/ebs/courses/courseaddedit";
</style>
